/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FontIcon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyles, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { UnsavedChangesPrompt } from 'src/core/components/UnsavedChangesPrompt';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { ChangeSavingStateEvent } from 'src/core/types/ConfirmLeaveEvent';
import { getEventBusV2 } from 'src/core/utils/eventBusV2';
import { routeTable } from 'src/core/utils/routeTable';
import { SavingState } from 'src/customText/appShell/dataTaggingPage/hooks/useSaveChanges';

const messages = defineMessages({
    dirtyStateMessageContent: {
        id: 'customText.savingIndicator.dirtyStateMessageContent',
        defaultMessage: 'You have unsaved changes.'
    },
    inProgressStateMessageContent: {
        id: 'customText.savingIndicator.inProgressStateMessageContent',
        defaultMessage: 'Saving changes...'
    },
    syncedStateMessageContent: {
        id: 'customText.savingIndicator.syncedStateMessageContent',
        defaultMessage: 'Saved'
    }
});

const theme = getScopedTheme('SavingIndicator');
const iconClass = mergeStyles({
    fontSize: 16
});

const syncIconClassNames = mergeStyleSets({
    dirty: [{ color: theme.dirtyColor }, iconClass],
    inProgress: [{ color: theme.inProgressColor }, iconClass],
    synced: [{ color: theme.syncedColor }, iconClass]
});

export type SavingIndicatorProps = {
    savingState: SavingState;
    onDiscard?: () => void;
};

const promptId = getId('saveLabelsPrompt');

/**
 * Icon representing the current saving state.
 */
export const SavingIndicator = observer((props: SavingIndicatorProps) => {
    const { localize } = useLocalization();
    const {
        rootStore: { currentCustomTextProjectStore }
    } = useGlobalState();
    const { savingState, onDiscard } = props;
    const eventBusV2 = getEventBusV2();

    let iconClassName;
    let messageContent;
    switch (savingState) {
        case 'dirty':
            iconClassName = syncIconClassNames.dirty;
            messageContent = localize(messages.dirtyStateMessageContent);
            break;
        case 'inProgress':
            iconClassName = syncIconClassNames.inProgress;
            messageContent = localize(messages.inProgressStateMessageContent);
            break;
        case 'saved':
            iconClassName = syncIconClassNames.synced;
            messageContent = localize(messages.syncedStateMessageContent);
            break;
        default:
    }

    React.useEffect(() => {
        eventBusV2.emit<ChangeSavingStateEvent>({
            savingState,
            id: promptId,
            kind: 'changeSavingState'
        });
    }, [savingState]);

    return (
        <>
            <UnsavedChangesPrompt
                onLeaveCb={onDiscard}
                isPageDirty={savingState === 'dirty'}
                promptPath={routeTable.customText.dataTaggingPage(
                    currentCustomTextProjectStore.id,
                    currentCustomTextProjectStore.projectKind
                )}
            />
            <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8 }}>
                <FontIcon
                    iconName={savingState === 'dirty' || savingState === 'inProgress' ? 'Warning' : 'CheckMark'}
                    className={iconClassName}
                />
                <LuisText>{messageContent}</LuisText>
            </Stack>
        </>
    );
});
